"use client";

import React from "react";
import { Box, Grid, Button, Typography, Stack } from "@mui/material";

import { useRouter } from "@/i18n/routing";
import { useTranslations } from "next-intl";

const PricingPlanSection = () => {
    const router = useRouter();
    const t = useTranslations('HomePage.pricing_plan_section');

    const onGoing = (id: string) => {
        if (!id) return;

        router.push(`/service/${id}`);
    }

    return (
        <Box sx={{ py: 6, backgroundColor: "#f2f5f880" }}>
            <Grid container sx={{ maxWidth: "lg", margin: "auto", px: { xs: 2.5, md: 0 } }}>
                {/* Header */}
                <Grid size={12} sx={{ textAlign: "center" }}>
                    <Box sx={{ textAlign: "center", mb: 8, }}>
                        <span className="inline-block bg-[#F15859] text-white text-xs font-bold px-6 py-1.5 rounded-full mb-6 shadow-sm">
                            {t("chip")}
                        </span>
                        <Typography variant="h4" component="h2" sx={{ fontWeight: 800, color: "#132c4b", mb: 2 }}>
                            {t("title")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#64748b", maxWidth: "90%", margin: "auto" }}>
                            {t("description_line_1")}
                            <br />
                            {t("description_line_2")}
                        </Typography>
                    </Box>
                </Grid>

                {/* Cards */}
                <Grid size={12}>
                    <Grid container spacing={4} justifyContent="center" alignItems="stretch">
                        {/* Fixed Monthly Plan */}
                        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    p: 4,
                                    borderRadius: "24px",
                                    backgroundColor: "white",
                                    border: "1px solid #e2e8f0",
                                    boxShadow: "none",
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 800, color: "#132c4b", mb: 3, textAlign: 'left' }}>
                                    {t("plan_title_1")}
                                </Typography>

                                <Box sx={{ mb: 3, p: 3, backgroundColor: "#f8fafc", borderRadius: "12px", textAlign: "left" }}>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#132c4b", mb: 1 }}>
                                        {t("plan_topic_1")}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>

                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#132c4b", mb: 1 }}>
                                                {t("plan_1_topic_1")}
                                            </Typography>
                                            <Stack spacing={1} sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography sx={{ color: "#F15859", fontWeight: "bold" }}>✓</Typography>
                                                    <Typography variant="body2" sx={{ color: "#475569" }}>
                                                        {t("plan_1_topic_1_item_1")}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography sx={{ color: "#F15859", fontWeight: "bold" }}>✓</Typography>
                                                    <Typography variant="body2" sx={{ color: "#475569" }}>
                                                        {t("plan_1_topic_1_item_2")}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#132c4b", mb: 1 }}>
                                                {t("plan_1_topic_2")}
                                            </Typography>
                                            <Stack spacing={1} sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography sx={{ color: "#F15859", fontWeight: "bold" }}>✓</Typography>
                                                    <Typography variant="body2" sx={{ color: "#475569" }}>
                                                        {t("plan_1_topic_2_item_1")}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography sx={{ color: "#F15859", fontWeight: "bold" }}>✓</Typography>
                                                    <Typography variant="body2" sx={{ color: "#475569" }}>
                                                        {t("plan_1_topic_2_item_2")}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography sx={{ color: "#F15859", fontWeight: "bold" }}>✓</Typography>
                                                    <Typography variant="body2" sx={{ color: "#475569" }}>
                                                        {t("plan_1_topic_2_item_3")}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid #f1f5f9' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#64748b", mb: 1 }}>
                                        {t("note")}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 2 }}>
                                        {t("plan_1_note_2")}
                                    </Typography>
                                    <Stack>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            sx={{
                                                backgroundColor: '#F15859',
                                                fontWeight: 'bold',
                                                py: 2,
                                                fontSize: '1rem',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.3)',
                                                '&:hover': {
                                                    backgroundColor: '#be123c'
                                                },
                                                maxWidth: '250px',
                                                mx: 'auto'
                                            }}
                                            onClick={() => onGoing('#lite-plan-section')}
                                        >
                                            {t("button_title")}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Full Management Plan */}
                        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    p: 4,
                                    borderRadius: "24px",
                                    backgroundColor: "white",
                                    border: "2px solid #F15859",
                                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -12,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: '#F15859',
                                        color: 'white',
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: '20px',
                                        fontSize: '0.875rem',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {t("recommand_title")}
                                </Box>

                                <Typography variant="h6" sx={{ fontWeight: 800, color: "#132c4b", mb: 3, textAlign: 'left' }}>
                                    {t("plan_title_2")}
                                </Typography>

                                <Box sx={{ mb: 3, p: 3, backgroundColor: "#f8fafc", borderRadius: "12px", textAlign: "left" }}>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#132c4b", mb: 1 }}>
                                        {t("plan_2_title_1")}
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#132c4b", mb: 1 }}>
                                                {t("plan_2_topic_1")}
                                            </Typography>
                                            <Stack spacing={1} sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography sx={{ color: "#F15859", fontWeight: "bold" }}>✓</Typography>
                                                    <Typography variant="body2" sx={{ color: "#475569" }}>
                                                        {t("plan_2_topic_1_item_1")}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#132c4b", mb: 1 }}>
                                                {t("plan_2_topic_2")}
                                            </Typography>
                                            <Stack spacing={1} sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography sx={{ color: "#F15859", fontWeight: "bold" }}>✓</Typography>
                                                    <Typography variant="body2" sx={{ color: "#475569" }}>
                                                        {t("plan_2_topic_2_item_1")}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid #f1f5f9' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#64748b", mb: 1 }}>
                                        {t("note")}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 2 }}>
                                        {t("plan_2_note_1")}
                                    </Typography>
                                    <Stack>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            sx={{
                                                backgroundColor: '#F15859',
                                                fontWeight: 'bold',
                                                py: 2,
                                                fontSize: '1rem',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.3)',
                                                '&:hover': {
                                                    backgroundColor: '#be123c'
                                                },
                                                maxWidth: '250px',
                                                mx: 'auto'
                                            }}
                                            onClick={() => onGoing('#ex-plan-section')}
                                        >
                                            {t("button_title")}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
}

export default PricingPlanSection;

